import { useCallback, useEffect, useState } from 'react';
import { invitationRoleOptions } from './tenant-access.js';

interface TeamMember {
  readonly membershipId: string;
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly status: string;
  readonly role: { readonly key: string; readonly name: string | null } | null;
}

export function OrganizationTeam({
  organizationId,
  refreshSignal,
  onChanged,
  onExpiredSession,
}: {
  readonly organizationId: string;
  readonly refreshSignal: number;
  readonly onChanged: () => Promise<void> | void;
  readonly onExpiredSession: () => Promise<void>;
}) {
  const [members, setMembers] = useState<readonly TeamMember[]>([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState<string>();
  const [error, setError] = useState<string>();
  const [feedback, setFeedback] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/v1/organizations/${organizationId}/admin/team`, {
        credentials: 'include',
      });
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) throw new Error(String(response.status));
      const body = (await response.json()) as { currentUserId: string; members: TeamMember[] };
      setCurrentUserId(body.currentUserId);
      setMembers(body.members);
      setError(undefined);
    } catch {
      setError('Não foi possível carregar a equipe.');
    } finally {
      setLoading(false);
    }
  }, [onExpiredSession, organizationId]);

  useEffect(() => {
    void load();
  }, [load, refreshSignal]);

  const update = async (member: TeamMember, kind: 'role' | 'status', value: string) => {
    setChanging(member.membershipId);
    setError(undefined);
    setFeedback(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/admin/team/${member.membershipId}/${kind}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(kind === 'role' ? { roleKey: value } : { status: value }),
        },
      );
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { code?: string } | null;
        setError(
          body?.code === 'LAST_ADMINISTRATOR_REQUIRED'
            ? 'Mantenha ao menos um administrador ativo antes de alterar este acesso.'
            : 'Não foi possível alterar o acesso. Tente novamente.',
        );
        return;
      }
      setFeedback(`Acesso de ${member.name} atualizado.`);
      await load();
      await onChanged();
    } catch {
      setError('Não foi possível alterar o acesso. Verifique sua conexão.');
    } finally {
      setChanging(undefined);
    }
  };

  return (
    <section className="teamPanel" aria-labelledby="team-title">
      <div className="resourcePanelHeading">
        <div>
          <span className="sectionEyebrow">3. Equipe e acesso</span>
          <h3 id="team-title">Quem pode trabalhar nesta empresa</h3>
          <p>Defina uma função por pessoa. Alterações não concedem acesso a outras empresas.</p>
        </div>
        <button
          className="secondaryButton"
          disabled={loading}
          type="button"
          onClick={() => void load()}
        >
          Atualizar
        </button>
      </div>
      {loading ? <p role="status">Carregando equipe…</p> : null}
      {members.map((member) => (
        <article className="teamMember" key={member.membershipId}>
          <div>
            <strong>
              {member.name}
              {member.userId === currentUserId ? ' (você)' : ''}
            </strong>
            <span>{member.email}</span>
            <small>
              {member.status === 'active'
                ? 'Acesso ativo'
                : member.status === 'suspended'
                  ? 'Acesso suspenso'
                  : 'Acesso revogado'}
            </small>
          </div>
          <div className="teamMemberActions">
            <label>
              Função
              <select
                disabled={member.status !== 'active' || changing === member.membershipId}
                value={member.role?.key ?? 'viewer'}
                onChange={(event) => void update(member, 'role', event.target.value)}
              >
                {invitationRoleOptions.map((role) => (
                  <option key={role.key} value={role.key}>
                    {role.name}
                  </option>
                ))}
              </select>
            </label>
            {member.status === 'active' ? (
              <button
                className="dangerButton"
                disabled={changing === member.membershipId}
                type="button"
                onClick={() => void update(member, 'status', 'suspended')}
              >
                Suspender acesso
              </button>
            ) : member.status === 'suspended' ? (
              <button
                className="secondaryButton"
                disabled={changing === member.membershipId}
                type="button"
                onClick={() => void update(member, 'status', 'active')}
              >
                Reativar acesso
              </button>
            ) : null}
          </div>
        </article>
      ))}
      {error ? (
        <p className="inlineError" role="alert">
          {error}
        </p>
      ) : null}
      {feedback ? (
        <p className="invitationFeedback invitationFeedback-success" role="status">
          {feedback}
        </p>
      ) : null}
    </section>
  );
}
